import type {ReactNode} from 'react';

import {Button} from '@/components';

import {
  formatDuplicateSender,
  type PoolDepositDuplicateWarning,
} from './poolDepositDuplicate';

type PoolDepositDuplicateBannerProps = {
  warning: PoolDepositDuplicateWarning;
  isLoading: boolean;
  onDismiss: VoidFunction;
  onConfirm: VoidFunction;
  confirmLabel: string;
  /** Prefix for this surface's test ids, e.g. `pool-review` / `pool-upload`. */
  testIdPrefix: string;
  /** The surface-specific explanation of what confirming again would do. */
  children: ReactNode;
  /** After Cancel: kept as a reminder, without its buttons. */
  isDismissed?: boolean;
};

/**
 * Shared "a matching pool deposit already exists" prompt.
 *
 * Both admin surfaces that can trip the server's duplicate guard (the
 * operator-typed confirm-deposit modal and the entity-submission review panel)
 * offer the same three things: the sender context, a way out, and a way
 * through. Only the message body and the confirm wording differ, so those are
 * the props; everything else stays one implementation.
 */
export default function PoolDepositDuplicateBanner({
  warning,
  isLoading,
  onDismiss,
  onConfirm,
  confirmLabel,
  testIdPrefix,
  children,
  isDismissed,
}: PoolDepositDuplicateBannerProps) {
  const existingSender = formatDuplicateSender(warning);

  return (
    <div
      className="mb-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 dark:border-yellow-700 dark:bg-yellow-900/20"
      data-testid={`${testIdPrefix}-duplicate-${isDismissed ? 'reminder' : 'warning'}`}
    >
      <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
        {children}
      </p>
      {existingSender && (
        <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
          Existing sender: {existingSender}
        </p>
      )}
      {!isDismissed && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            colorScheme="cyan"
            onClick={onDismiss}
            disabled={isLoading}
            data-testid={`${testIdPrefix}-duplicate-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="solid"
            colorScheme="red"
            onClick={onConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            loadingText="Confirming…"
            data-testid={`${testIdPrefix}-duplicate-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
